import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { authStore, type DashboardAuth } from '../../auth/authStore';
import { GraphQLRequestError } from '../../graphql/routing/apiRouting';
import { readDashboardAccountScope } from '../voters/voterAccountScope';
import {
  createCanvassingSessionTranscriptionApi,
  getCanvassingSessionTranscriptionError,
  type CanvassingSessionTranscriptionApi,
  type CanvassingSessionTranscriptionError,
} from './canvassingSessionTranscriptionApi';
import type {
  CanvassingSessionTranscriptionPublication,
  CanvassingSessionTranscriptionResultBody,
} from './sessionTranscriptionTypes';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import { isCanvassingSessionId } from './useCanvassingSessionDetail';

export const CANVASSING_SESSION_TRANSCRIPTION_POLL_INTERVAL_MS = 5_000;
export const CANVASSING_SESSION_TRANSCRIPTION_POLL_WINDOW_MS = 5 * 60_000;

export type CanvassingSessionTranscriptionState =
  | { status: 'idle' }
  | { status: 'loading'; publication: CanvassingSessionTranscriptionPublication | null }
  | { status: 'deadline' }
  | { status: 'unavailable' }
  | { status: 'forbidden' }
  | {
      status: 'error';
      error: CanvassingSessionTranscriptionError;
      publication: CanvassingSessionTranscriptionPublication | null;
      uncertainSubmission: boolean;
      authoritativeSubmissionFailure: boolean;
    }
  | { status: 'ready'; publication: CanvassingSessionTranscriptionPublication; refreshing: boolean; pollingTimedOut: boolean };

export type UseCanvassingSessionTranscriptionOptions = {
  sessionId: string | undefined;
  permitted: boolean;
  fetchImpl?: CanvassingSessionFetchImpl;
  api?: CanvassingSessionTranscriptionApi;
};

export type UseCanvassingSessionTranscriptionResult = {
  state: CanvassingSessionTranscriptionState;
  refresh: () => void;
  submit: () => Promise<boolean>;
};

const isActiveStatus = (status: string | undefined): boolean =>
  status === 'QUEUED' || status === 'SUBMITTING' || status === 'PROCESSING';

const sameRevision = (
  result: CanvassingSessionTranscriptionResultBody | null,
  publication: CanvassingSessionTranscriptionPublication,
): boolean => result !== null
  ? result.runId === publication.publishedRunId && result.revision === publication.publishedRevision
  : publication.publishedRunId === null && publication.publishedRevision === 0;

const publicationWithExistingBody = (
  next: CanvassingSessionTranscriptionPublication,
  previous: CanvassingSessionTranscriptionPublication | null,
): CanvassingSessionTranscriptionPublication | null => {
  if (sameRevision(previous?.result ?? null, next)) return { ...next, result: previous?.result ?? null };
  if (sameRevision(next.result, next)) return next;
  return null;
};

const subscribeAuth = (onChange: () => void) => authStore.subscribe((_auth: DashboardAuth | null) => onChange());

const isAuthoritativeSubmissionFailure = (error: unknown): boolean => {
  if (!(error instanceof GraphQLRequestError) || error.kind !== 'graphql') return false;
  const code = getCanvassingSessionTranscriptionError(error).failureCode;
  return code !== null && [
    'CORE_CONFIGURATION_REQUIRED',
    'RECORDING_UNAVAILABLE',
    'RECORDING_IDENTITY_INVALID',
    'CORE_REJECTED',
    'CORE_RESPONSE_INVALID',
    'SUBMISSION_FAILED',
    'ANALYSIS_TIMEOUT',
    'SESSION_TRANSCRIPTION_IN_PROGRESS',
  ].includes(code);
};

export const useCanvassingSessionTranscription = ({
  sessionId,
  permitted,
  fetchImpl,
  api: apiOverride,
}: UseCanvassingSessionTranscriptionOptions): UseCanvassingSessionTranscriptionResult => {
  const auth = useSyncExternalStore(subscribeAuth, authStore.getAuth);
  const authScope = readDashboardAccountScope(auth);
  const scopeKey = `${sessionId ?? 'no-session'}:${authScope}`;
  const api = useMemo(() => apiOverride ?? createCanvassingSessionTranscriptionApi(fetchImpl), [apiOverride, fetchImpl]);
  const [state, setState] = useState<CanvassingSessionTranscriptionState>({ status: 'idle' });
  const stateScopeRef = useRef(scopeKey);
  const permissionDeniedScopeRef = useRef<string | null>(null);
  const actionsRef = useRef<{ refresh: () => void; submit: () => Promise<boolean> }>({
    refresh: () => undefined,
    submit: async () => false,
  });

  const refresh = useCallback(() => actionsRef.current.refresh(), []);
  const submit = useCallback(() => actionsRef.current.submit(), []);

  useEffect(() => {
    stateScopeRef.current = scopeKey;
    if (!permitted || !isCanvassingSessionId(sessionId) || permissionDeniedScopeRef.current === scopeKey) {
      setState({ status: 'idle' });
      actionsRef.current = { refresh: () => undefined, submit: async () => false };
      return undefined;
    }

    let mounted = true;
    let generation = 0;
    let controller: AbortController | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let deadline = 0;
    let inFlight = false;
    let requestId: string | null = null;
    let publication: CanvassingSessionTranscriptionPublication | null = null;
    let deadlineTimer: ReturnType<typeof setTimeout> | null = null;

    const clearPoll = (): void => {
      if (pollTimer === null) return;
      clearTimeout(pollTimer);
      pollTimer = null;
    };

    const isCurrent = (requestController: AbortController, requestGeneration: number): boolean =>
      mounted && !requestController.signal.aborted && controller === requestController && generation === requestGeneration;

    const abortCurrent = (): void => {
      generation += 1;
      inFlight = false;
      const previous = controller;
      controller = null;
      previous?.abort();
    };

    const clearDeadline = (): void => {
      if (deadlineTimer === null) return;
      clearTimeout(deadlineTimer);
      deadlineTimer = null;
    };

    const startDeadline = (): void => {
      clearDeadline();
      deadline = Date.now() + CANVASSING_SESSION_TRANSCRIPTION_POLL_WINDOW_MS;
      deadlineTimer = setTimeout(() => {
        deadlineTimer = null;
        clearPoll();
        if (inFlight) abortCurrent();
        if (publication === null) {
          setState({ status: 'deadline' });
          return;
        }
        const pollingTimedOut = isActiveStatus(publication.latestRun?.status);
        setState({ status: 'ready', publication, refreshing: false, pollingTimedOut });
        if (!pollingTimedOut) clearDeadline();
      }, CANVASSING_SESSION_TRANSCRIPTION_POLL_WINDOW_MS);
    };

    const stopForPermission = (): void => {
      permissionDeniedScopeRef.current = scopeKey;
      clearPoll();
      clearDeadline();
      abortCurrent();
      publication = null;
      setState({ status: 'forbidden' });
      actionsRef.current = { refresh: () => undefined, submit: async () => false };
    };

    const schedulePoll = (): void => {
      clearPoll();
      if (!mounted || inFlight) return;
      if (!isActiveStatus(publication?.latestRun?.status)) {
        clearDeadline();
        return;
      }
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        if (publication !== null) setState({ status: 'ready', publication, refreshing: false, pollingTimedOut: true });
        return;
      }
      pollTimer = setTimeout(() => {
        pollTimer = null;
        if (Date.now() >= deadline) {
          if (publication !== null) setState({ status: 'ready', publication, refreshing: false, pollingTimedOut: true });
          return;
        }
        void requestPublication('automatic', false);
      }, Math.min(CANVASSING_SESSION_TRANSCRIPTION_POLL_INTERVAL_MS, remaining));
    };

    const reportIncoherentRevision = (): void => {
      controller = null;
      inFlight = false;
      clearDeadline();
      setState({
        status: 'error',
        error: { kind: 'request', failureCode: null },
        publication,
        uncertainSubmission: false,
        authoritativeSubmissionFailure: false,
      });
    };

    const settlePublication = (
      incoming: CanvassingSessionTranscriptionPublication | null,
      requestController: AbortController,
      requestGeneration: number,
      kind: 'initial' | 'automatic' | 'manual' | 'submitted',
      consistencyAttempts: number,
    ): void => {
      if (!isCurrent(requestController, requestGeneration)) return;
      controller = null;
      inFlight = false;
      if (incoming === null) {
        publication = null;
        clearDeadline();
        setState({ status: 'unavailable' });
        return;
      }

      const coherent = publicationWithExistingBody(incoming, publication);
      if (coherent === null) {
        controller = null;
        inFlight = false;
        if (consistencyAttempts >= 2) {
          reportIncoherentRevision();
          return;
        }
        void requestPublication(kind, true, false, consistencyAttempts + 1);
        return;
      }
      publication = coherent;
      setState({
        status: 'ready',
        publication: coherent,
        refreshing: false,
        pollingTimedOut: Date.now() >= deadline && isActiveStatus(coherent.latestRun?.status),
      });
      schedulePoll();
    };

    const requestPublication = async (
      kind: 'initial' | 'automatic' | 'manual' | 'submitted',
      includeResult: boolean,
      replace = false,
      consistencyAttempts = 0,
    ): Promise<void> => {
      if (!mounted || inFlight && !replace) return;
      if (inFlight) abortCurrent();
      clearPoll();
      const requestController = new AbortController();
      const requestGeneration = ++generation;
      controller = requestController;
      inFlight = true;
      if (publication === null) setState({ status: 'loading', publication: null });
      else setState({ status: 'ready', publication, refreshing: true, pollingTimedOut: false });

      try {
        const response = await api.fetchTranscription({ sessionId, includeResult }, { signal: requestController.signal });
        if (!isCurrent(requestController, requestGeneration)) return;
        if (response !== null && response.sessionId !== sessionId) {
          settlePublication(null, requestController, requestGeneration, kind, consistencyAttempts);
          return;
        }
        if (response !== null && !includeResult && !sameRevision(publication?.result ?? null, response)) {
          controller = null;
          inFlight = false;
          if (consistencyAttempts >= 2) {
            reportIncoherentRevision();
            return;
          }
          await requestPublication(kind, true, false, consistencyAttempts + 1);
          return;
        }
        settlePublication(response, requestController, requestGeneration, kind, consistencyAttempts);
      } catch (error: unknown) {
        if (!isCurrent(requestController, requestGeneration)) return;
        controller = null;
        inFlight = false;
        const requestError = getCanvassingSessionTranscriptionError(error);
        if (requestError.kind === 'forbidden') {
          stopForPermission();
          return;
        }
        clearDeadline();
        setState({ status: 'error', error: requestError, publication, uncertainSubmission: false, authoritativeSubmissionFailure: false });
      }
    };

    const beginSubmit = async (): Promise<boolean> => {
      if (!mounted || permissionDeniedScopeRef.current === scopeKey) return false;
      clearDeadline();
      clearPoll();
      abortCurrent();
      const requestController = new AbortController();
      const requestGeneration = ++generation;
      controller = requestController;
      inFlight = true;
      if (publication !== null) setState({ status: 'ready', publication, refreshing: true, pollingTimedOut: false });
      try {
        requestId ??= crypto.randomUUID();
      } catch {
        controller = null;
        inFlight = false;
        clearDeadline();
        setState({ status: 'error', error: { kind: 'request', failureCode: null }, publication, uncertainSubmission: false, authoritativeSubmissionFailure: false });
        return false;
      }
      try {
        const run = await api.transcribe({ sessionId, requestId }, { signal: requestController.signal });
        if (!isCurrent(requestController, requestGeneration)) return false;
        controller = null;
        inFlight = false;
        if (run === null) {
          requestId = null;
          clearDeadline();
          setState({ status: 'error', error: { kind: 'request', failureCode: null }, publication, uncertainSubmission: false, authoritativeSubmissionFailure: false });
          return false;
        }
        requestId = null;
        publication = publication === null ? null : { ...publication, latestRun: run };
        if (publication !== null) {
          setState({ status: 'ready', publication, refreshing: true, pollingTimedOut: false });
        }
        startDeadline();
        void requestPublication('submitted', true);
        return true;
      } catch (error: unknown) {
        if (!isCurrent(requestController, requestGeneration)) return false;
        controller = null;
        inFlight = false;
        const requestError = getCanvassingSessionTranscriptionError(error);
        if (requestError.kind === 'forbidden') {
          requestId = null;
          stopForPermission();
          return false;
        }
        clearDeadline();
        const authoritativeFailure = isAuthoritativeSubmissionFailure(error);
        if (authoritativeFailure) requestId = null;
        setState({
          status: 'error',
          error: requestError,
          publication,
          uncertainSubmission: !authoritativeFailure,
          authoritativeSubmissionFailure: authoritativeFailure && requestError.failureCode !== 'SESSION_TRANSCRIPTION_IN_PROGRESS',
        });
        return false;
      }
    };

    actionsRef.current = {
      refresh: () => {
        startDeadline();
        void requestPublication('manual', true, true);
      },
      submit: beginSubmit,
    };

    setState({ status: 'loading', publication: null });
    startDeadline();
    void requestPublication('initial', true);

    return () => {
      mounted = false;
      actionsRef.current = { refresh: () => undefined, submit: async () => false };
      clearPoll();
      clearDeadline();
      abortCurrent();
    };
  }, [api, permitted, scopeKey, sessionId]);

  if (stateScopeRef.current !== scopeKey) return { state: { status: 'idle' }, refresh, submit };
  return { state, refresh, submit };
};
